import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { mockProjects, mockUsers, mockCompanies, mockCategories, mockTasks } from '../mock/data';
import { 
  Plus, ChevronDown, ChevronRight, Calendar, Users, Star, 
  MoreVertical, Sparkles, Building2, AlertTriangle, Edit2, 
  Trash2, Tag, Copy, Archive, Search, CheckCircle2, AlertCircle, 
  Clock, Pause, XCircle, ChevronsUpDown, ChevronsDownUp, Layers, List
} from 'lucide-react';
import AIProjectCreation from '../components/AIProjectCreation';
import { useTheme } from '../context/ThemeContext';
import HeaderGlow from '../components/HeaderGlow';

// Calculate project status based on tasks and dates
const calculateProjectStatus = (project, tasks) => {
  if (project.manualStatus) {
    return project.manualStatus;
  }
  
  const today = new Date();
  const endDate = new Date(project.endDate);
  
  if (today > endDate && project.progress < 100) {
    return 'late';
  }
  
  const projectTasks = tasks.filter(t => t.projectId === project.id);
  const hasLateTasks = projectTasks.some(task => {
    const taskDue = new Date(task.dueDate);
    return taskDue < today && task.status !== 'completed';
  });
  
  if (hasLateTasks) {
    return 'late-task';
  }
  
  if (project.progress === 100) {
    return 'completed';
  }
  
  return 'on-time';
};

const getStatusConfig = (status) => {
  switch (status) {
    case 'late':
      return { label: 'Late', color: 'bg-red-500/20 text-red-400', icon: AlertCircle };
    case 'late-task':
      return { label: 'Late Task', color: 'bg-orange-500/20 text-orange-400', icon: AlertTriangle };
    case 'on-time':
      return { label: 'On Time', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle2 };
    case 'on-hold':
      return { label: 'On Hold', color: 'bg-amber-500/20 text-amber-400', icon: Pause };
    case 'cancelled':
      return { label: 'Cancelled', color: 'bg-slate-500/20 text-slate-400', icon: XCircle };
    case 'completed':
      return { label: 'Completed', color: 'bg-blue-500/20 text-blue-400', icon: CheckCircle2 };
    default:
      return { label: 'Unknown', color: 'bg-slate-500/20 text-slate-400', icon: Clock };
  }
};

const Projects = () => {
  const navigate = useNavigate();
  // Default: first company expanded, others collapsed
  const [expandedCompanies, setExpandedCompanies] = useState(() => {
    return mockCompanies.length > 0 ? [mockCompanies[0].id] : [];
  });
  const [open, setOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [groupByCompany, setGroupByCompany] = useState(true); // Toggle for company headers

  const toggleCompany = (companyId) => {
    setExpandedCompanies(prev =>
      prev.includes(companyId) ? prev.filter(id => id !== companyId) : [...prev, companyId]
    );
  };

  // Collapse/Expand all companies
  const toggleAllCompanies = () => {
    const allCompanyIds = projectsByCompany.map(({ company }) => company.id);
    const allExpanded = allCompanyIds.every(id => expandedCompanies.includes(id));
    if (allExpanded) {
      setExpandedCompanies([]);
    } else {
      setExpandedCompanies(allCompanyIds);
    }
  };

  const areAllExpanded = () => {
    const allCompanyIds = projectsByCompany.map(({ company }) => company.id);
    return allCompanyIds.length > 0 && allCompanyIds.every(id => expandedCompanies.includes(id));
  };

  // Filter projects by search and company
  const filteredProjects = mockProjects.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCompany = companyFilter === 'all' || p.companyId === companyFilter;
    return matchesSearch && matchesCompany;
  });

  // Group projects by company
  const projectsByCompany = mockCompanies.map(company => ({
    company,
    projects: filteredProjects.filter(p => p.companyId === company.id)
  })).filter(group => group.projects.length > 0);

  // Get company name by ID
  const getCompanyName = (companyId) => {
    const company = mockCompanies.find(c => c.id === companyId);
    return company?.name || 'Unknown';
  };

  const handleEditProject = (project) => {
    setEditProject(project);
  };

  const handleDeleteProject = (project) => {
    if (window.confirm(`Are you sure you want to delete "${project.name}"?`)) {
      alert('Project deletion will be implemented with backend');
    }
  };

  const handleStatusChange = (projectId, newStatus) => {
    // In a real app, this would update the backend
    // For now, we show what would happen
    const statusLabel = newStatus === null ? 'Auto (Calculated)' : newStatus;
    alert(`Status for project would be set to: ${statusLabel}\n\nThis will be implemented with backend integration.`);
  };

  const { currentColorTheme } = useTheme();

  return (
    <div className="p-8 bg-background min-h-screen">
      {/* Dynamic Header - uses theme colors */}
      <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-purple-500 via-violet-500 to-fuchsia-500'} -mx-8 -mt-8 p-8`}>
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Projects</h1>
            <p className="text-white/80">Manage and track all your projects by company</p>
          </div>
          <div className="text-right mr-4">
            <p className="text-4xl font-bold text-white">{filteredProjects.length}</p>
            <p className="text-white/80 text-sm">Total Projects</p>
          </div>
        </div>
      </div>
      {/* Glowing separator line */}
      <div className="-mx-8">
        <HeaderGlow />
      </div>

      {/* Toolbar - with top margin for spacing */}
      <div className="flex items-center justify-between mb-6 mt-6">
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-[280px]"
            />
          </div>
          {/* Company Filter */}
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger className="w-[200px]">
              <Building2 className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Companies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {mockCompanies.map(company => (
                <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Group by Company Toggle */}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setGroupByCompany(!groupByCompany)}
            className={`flex items-center gap-2 ${groupByCompany ? 'bg-primary/10 border-primary' : ''}`}
            data-testid="toggle-group-by-company"
          >
            {groupByCompany ? (
              <>
                <Layers className="w-4 h-4" />
                <span className="hidden sm:inline">Grouped</span>
              </>
            ) : (
              <>
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">Flat List</span>
              </>
            )}
          </Button>
          {/* Collapse/Expand All Button - only show when grouped */}
          {groupByCompany && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={toggleAllCompanies}
              className="flex items-center gap-2"
              data-testid="toggle-all-companies"
            >
              {areAllExpanded() ? (
                <>
                  <ChevronsDownUp className="w-4 h-4" />
                  <span className="hidden sm:inline">Collapse All</span>
                </>
              ) : (
                <>
                  <ChevronsUpDown className="w-4 h-4" />
                  <span className="hidden sm:inline">Expand All</span>
                </>
              )}
            </Button>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={() => setAiOpen(true)} className="border-secondary text-secondary hover:bg-secondary/10">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Create
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-card">
              <DialogHeader>
                <DialogTitle className="text-foreground">Create New Project</DialogTitle>
              </DialogHeader>
              <ProjectForm onClose={() => setOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Projects Display - Grouped or Flat List */}
      <div className="space-y-6">
        {groupByCompany ? (
          // Grouped by Company View
          projectsByCompany.map(({ company, projects }) => {
            const isExpanded = expandedCompanies.includes(company.id);
            const totalProgress = projects.reduce((sum, p) => sum + p.progress, 0);
            const avgProgress = Math.round(totalProgress / projects.length);

            return (
              <Card key={company.id} className="bg-card border-border overflow-hidden">
                {/* Company Header */}
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors border-b border-border"
                  onClick={() => toggleCompany(company.id)}
                >
                  <div className="flex items-center space-x-4">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    )}
                    <div>
                      <h3 className="font-semibold text-foreground">{company.name}</h3>
                      <p className="text-sm text-muted-foreground">{company.industry}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">{projects.length} projects</p>
                      <p className="text-xs text-muted-foreground">Avg: {avgProgress}%</p>
                    </div>
                    <div className="w-32 h-4 bg-muted/50 rounded-full overflow-hidden relative">
                      <div 
                        className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-secondary'}`}
                        style={{ width: `${avgProgress}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Projects List */}
                {isExpanded && (
                  <div className="divide-y divide-border">
                    {projects.map((project) => (
                      <ProjectRow 
                        key={project.id} 
                        project={project} 
                        showCompany={false}
                        navigate={navigate}
                        handleEditProject={handleEditProject}
                        handleDeleteProject={handleDeleteProject}
                        handleStatusChange={handleStatusChange}
                        currentColorTheme={currentColorTheme}
                      />
                    ))}
                  </div>
                )}
              </Card>
            );
          })
        ) : (
          // Flat List View with Company Column
          <Card className="bg-card border-border overflow-hidden">
            {/* Header Row */}
            <div className="flex items-center p-4 border-b border-border bg-muted/30 text-sm font-medium text-muted-foreground">
              <div className="w-[140px] flex-shrink-0">COMPANY</div>
              <div className="w-[320px] flex-shrink-0">PROJECT</div>
              <div className="w-[120px] flex-shrink-0">TAGS</div>
              <div className="w-[120px] flex-shrink-0">PM</div>
              <div className="w-[100px] flex-shrink-0">STATUS</div>
              <div className="w-[120px] flex-shrink-0 px-4">PROGRESS</div>
              <div className="w-[80px] flex-shrink-0 text-center">TEAM</div>
              <div className="w-[40px] flex-shrink-0"></div>
            </div>
            <div className="divide-y divide-border">
              {filteredProjects.map((project) => (
                <ProjectRow 
                  key={project.id} 
                  project={project} 
                  showCompany={true}
                  getCompanyName={getCompanyName}
                  navigate={navigate}
                  handleEditProject={handleEditProject}
                  handleDeleteProject={handleDeleteProject}
                  handleStatusChange={handleStatusChange}
                  currentColorTheme={currentColorTheme}
                />
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Empty State */}
      {projectsByCompany.length === 0 && (
        <div className="text-center py-16">
          <Building2 className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No projects found</h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery ? 'Try adjusting your search' : 'Get started by creating your first project'}
          </p>
        </div>
      )}

      {/* Edit Project Dialog */}
      <Dialog open={!!editProject} onOpenChange={(open) => !open && setEditProject(null)}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-card">
          <DialogHeader>
            <DialogTitle className="text-foreground">Edit Project</DialogTitle>
          </DialogHeader>
          {editProject && <ProjectForm project={editProject} onClose={() => setEditProject(null)} />}
        </DialogContent>
      </Dialog>

      {/* AI Create Dialog */}
      <AIProjectCreation open={aiOpen} onOpenChange={setAiOpen} />
    </div>
  );
};

// Project Row Component - Reusable for both grouped and flat list views
const ProjectRow = ({ 
  project, 
  showCompany, 
  getCompanyName, 
  navigate, 
  handleEditProject, 
  handleDeleteProject, 
  handleStatusChange,
  currentColorTheme 
}) => {
  const teamMembers = project.teamMembers.map(id => mockUsers.find(u => u.id === id));
  const status = calculateProjectStatus(project, mockTasks);
  const statusConfig = getStatusConfig(status);
  const StatusIcon = statusConfig.icon;
  const projectManager = mockUsers.find(u => u.id === project.projectManagerId);

  return (
    <div className="flex items-center p-4 hover:bg-muted/20 transition-colors group">
      {/* Company Column - Only in flat list view */}
      {showCompany && (
        <div className="w-[140px] flex-shrink-0">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground truncate">
              {getCompanyName(project.companyId)}
            </span>
          </div>
        </div>
      )}

      {/* Project Name Section */}
      <div 
        className={`flex items-center space-x-4 ${showCompany ? 'w-[320px]' : 'w-[400px]'} flex-shrink-0 cursor-pointer`}
        onClick={() => navigate(`/projects/${project.id}/details`)}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            // Toggle favorite
          }}
          className="text-muted-foreground hover:text-yellow-400 transition-colors"
        >
          <Star className={`w-5 h-5 ${project.isFavorite ? 'fill-yellow-400 text-yellow-400' : ''}`} />
        </button>
        <div 
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: project.color }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-foreground truncate">{project.name}</h4>
            {project.isEscalated && (
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            )}
          </div>
          <p className="text-sm text-muted-foreground truncate">{project.description}</p>
        </div>
      </div>

      {/* Tags */}
      <div className={`flex items-center gap-2 ${showCompany ? 'w-[120px]' : 'w-[140px]'} flex-shrink-0`}>
        {project.tags?.slice(0, 2).map(tag => (
          <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
        ))}
      </div>

      {/* Project Manager */}
      <div className={`flex items-center gap-2 ${showCompany ? 'w-[120px]' : 'w-[140px]'} flex-shrink-0`}>
        {projectManager && (
          <>
            <Avatar className="w-6 h-6">
              <AvatarImage src={projectManager.avatar} />
              <AvatarFallback className="text-xs">{projectManager.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <span className="text-sm text-muted-foreground truncate">{projectManager.name?.split(' ')[0]}</span>
          </>
        )}
      </div>

      {/* Status */}
      <div className={`${showCompany ? 'w-[100px]' : 'w-[120px]'} flex-shrink-0`} onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={`${statusConfig.color} text-xs h-7 px-2 hover:opacity-80`}
            >
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
              <ChevronDown className="w-3 h-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-40">
            <DropdownMenuItem onClick={() => handleStatusChange(project.id, null)}>
              <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" />
              Auto (Calculated)
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleStatusChange(project.id, 'on-hold')}>
              <Pause className="w-4 h-4 mr-2 text-amber-500" />
              On Hold
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleStatusChange(project.id, 'cancelled')}>
              <XCircle className="w-4 h-4 mr-2 text-slate-500" />
              Cancelled
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleStatusChange(project.id, 'completed')}>
              <CheckCircle2 className="w-4 h-4 mr-2 text-blue-500" />
              Completed
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Progress */}
      <div className={`${showCompany ? 'w-[120px]' : 'w-[140px]'} flex-shrink-0 px-4`}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">Progress</span>
          <span className="text-xs font-semibold text-foreground">{project.progress}%</span>
        </div>
        <div className="h-2 bg-muted/50 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-secondary'} transition-all`}
            style={{ width: `${project.progress}%` }}
          />
        </div>
      </div>

      {/* Team Members */}
      <div className={`flex -space-x-2 ${showCompany ? 'w-[80px]' : 'w-[100px]'} flex-shrink-0 justify-center`}>
        {teamMembers.slice(0, 3).map((member) => (
          <Avatar key={member?.id} className="w-7 h-7 border-2 border-background">
            <AvatarImage src={member?.avatar} />
            <AvatarFallback className="text-xs">{member?.name?.charAt(0)}</AvatarFallback>
          </Avatar>
        ))}
        {teamMembers.length > 3 && (
          <div className="w-7 h-7 rounded-full bg-muted border-2 border-background flex items-center justify-center">
            <span className="text-xs text-muted-foreground">+{teamMembers.length - 3}</span>
          </div>
        )}
      </div>

      {/* Actions Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="opacity-0 group-hover:opacity-100 transition-opacity ml-2"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => handleEditProject(project)}>
            <Edit2 className="w-4 h-4 mr-2" />
            Edit Project
          </DropdownMenuItem>
          <DropdownMenuItem>
            <Tag className="w-4 h-4 mr-2" />
            Manage Tags
          </DropdownMenuItem>
          <DropdownMenuItem>
            <Users className="w-4 h-4 mr-2" />
            Manage Team
          </DropdownMenuItem>
          <DropdownMenuItem>
            <Copy className="w-4 h-4 mr-2" />
            Duplicate
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem>
            <Archive className="w-4 h-4 mr-2" />
            Archive
          </DropdownMenuItem>
          <DropdownMenuItem 
            className="text-destructive"
            onClick={() => handleDeleteProject(project)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// Project Form Component
const ProjectForm = ({ project, onClose }) => {
  const [formData, setFormData] = useState({
    name: project?.name || '',
    description: project?.description || '',
    scopeOfWork: project?.scopeOfWork || '',
    deliverables: project?.deliverables || '',
    companyId: project?.companyId || '',
    categoryId: project?.categoryId || '',
    projectManagerId: project?.projectManagerId || '',
    startDate: project?.startDate || '',
    endDate: project?.endDate || '',
    manualStatus: project?.manualStatus || '',
    tags: project?.tags?.join(', ') || '',
    color: project?.color || '#10b981'
  });

  const handleSubmit = () => {
    // Update the mock project data (in real app, this would be an API call)
    if (project) {
      const projectIndex = mockProjects.findIndex(p => p.id === project.id);
      if (projectIndex !== -1) {
        mockProjects[projectIndex] = {
          ...mockProjects[projectIndex],
          ...formData,
          tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
          manualStatus: formData.manualStatus || null
        };
      }
    }
    onClose();
  };

  return (
    <div className="space-y-4 mt-4">
      <div>
        <Label className="text-foreground">Project Name *</Label>
        <Input 
          placeholder="Enter project name" 
          className="mt-1"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
        />
      </div>
      <div>
        <Label className="text-foreground">Description</Label>
        <Textarea 
          placeholder="Brief project description" 
          className="mt-1" 
          rows={2}
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-foreground">Company *</Label>
          <Select value={formData.companyId} onValueChange={(v) => setFormData({...formData, companyId: v})}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select company" />
            </SelectTrigger>
            <SelectContent>
              {mockCompanies.map(company => (
                <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-foreground">Project Manager *</Label>
          <Select value={formData.projectManagerId} onValueChange={(v) => setFormData({...formData, projectManagerId: v})}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select PM" />
            </SelectTrigger>
            <SelectContent>
              {mockUsers.map(user => (
                <SelectItem key={user.id} value={user.id}>
                  <div className="flex items-center gap-2">
                    <Avatar className="w-5 h-5">
                      <AvatarImage src={user.avatar} />
                      <AvatarFallback className="text-xs">{user.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    {user.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-foreground">Scope of Work</Label>
        <Textarea 
          placeholder="Detailed scope of work" 
          className="mt-1" 
          rows={3}
          value={formData.scopeOfWork}
          onChange={(e) => setFormData({...formData, scopeOfWork: e.target.value})}
        />
      </div>
      <div>
        <Label className="text-foreground">Deliverables</Label>
        <Textarea 
          placeholder="Expected deliverables" 
          className="mt-1" 
          rows={3}
          value={formData.deliverables}
          onChange={(e) => setFormData({...formData, deliverables: e.target.value})}
        />
      </div>
      <div>
        <Label className="text-foreground">Tags</Label>
        <Input 
          placeholder="e.g., web, mobile, design (comma separated)" 
          className="mt-1"
          value={formData.tags}
          onChange={(e) => setFormData({...formData, tags: e.target.value})}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-foreground">Category</Label>
          <Select value={formData.categoryId} onValueChange={(v) => setFormData({...formData, categoryId: v})}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {mockCategories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-foreground">Manual Status Override</Label>
          <Select value={formData.manualStatus || 'auto'} onValueChange={(v) => setFormData({...formData, manualStatus: v === 'auto' ? '' : v})}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Auto (based on progress)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto (based on progress)</SelectItem>
              <SelectItem value="on-hold">On Hold</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-foreground">Start Date</Label>
          <Input 
            type="date" 
            className="mt-1"
            value={formData.startDate}
            onChange={(e) => setFormData({...formData, startDate: e.target.value})}
          />
        </div>
        <div>
          <Label className="text-foreground">End Date</Label>
          <Input 
            type="date" 
            className="mt-1"
            value={formData.endDate}
            onChange={(e) => setFormData({...formData, endDate: e.target.value})}
          />
        </div>
      </div>
      <div>
        <Label className="text-foreground">Color</Label>
        <div className="flex space-x-2 mt-2">
          {['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'].map(color => (
            <button
              key={color}
              type="button"
              className={`w-8 h-8 rounded-full border-2 transition-all ${formData.color === color ? 'border-foreground scale-110' : 'border-border'}`}
              style={{ backgroundColor: color }}
              onClick={() => setFormData({...formData, color})}
            />
          ))}
        </div>
      </div>
      <Button 
        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" 
        onClick={handleSubmit}
      >
        {project ? 'Update Project' : 'Create Project'}
      </Button>
    </div>
  );
};

export default Projects;
